import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Car, Loader2, AlertCircle } from 'lucide-react'
import { toast } from 'sonner'
import * as Sentry from '@sentry/react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import { Button } from '@/components/ui/button'
import type { Profile } from '@/types/database'

// ─── Helpers ──────────────────────────────────────────────────────────────────

function dashboardFor(role: Profile['role']): string {
  if (role === 'admin') return '/admin'
  if (role === 'instructor') return '/instrutor/dashboard'
  return '/aluno/dashboard'
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function AuthCallbackPage() {
  const navigate = useNavigate()
  const { profile } = useAuth()
  const [error, setError] = useState<string | null>(null)
  // Track whether the code exchange has been attempted (avoids StrictMode double-run)
  const exchangedRef = useRef(false)

  // ── Step 1: exchange the PKCE code for a session ───────────────────────────
  useEffect(() => {
    if (exchangedRef.current) return
    exchangedRef.current = true

    const code = new URLSearchParams(window.location.search).get('code')

    if (!code) {
      setError(
        'Código de autenticação não encontrado. O link pode ter expirado — tente fazer login novamente.',
      )
      return
    }

    supabase.auth.exchangeCodeForSession(code).then(({ error: exchangeError }) => {
      if (exchangeError) {
        Sentry.captureException(exchangeError, { tags: { source: 'AuthCallback.exchangeCodeForSession' } })
        if (import.meta.env.DEV) {
          console.error('[AuthCallback] exchangeCodeForSession error:', exchangeError.message)
        }
        setError(
          exchangeError.message === 'invalid request: both auth code and code verifier should be non-empty'
            ? 'Sessão expirada ou já utilizada. Por favor, faça login novamente.'
            : exchangeError.message,
        )
      }
      // On success: AuthContext's onAuthStateChange fires SIGNED_IN → fetches profile.
      // The effect below handles the redirect once profile is available.
    })
  }, [])

  // ── Step 2: redirect once AuthContext has the profile ─────────────────────
  useEffect(() => {
    if (!profile) return
    toast.success('Bem-vindo!')
    navigate(dashboardFor(profile.role), { replace: true })
  }, [profile, navigate])

  // ── Error screen ──────────────────────────────────────────────────────────
  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-5 p-6 text-center bg-gray-50">
        <div className="bg-brand-600 rounded-2xl p-3 shadow-md">
          <Car className="h-8 w-8 text-white" />
        </div>
        <div className="flex flex-col items-center gap-2">
          <AlertCircle className="h-10 w-10 text-destructive" />
          <h2 className="text-lg font-semibold text-gray-900">Falha na autenticação</h2>
          <p className="text-sm text-muted-foreground max-w-sm">{error}</p>
        </div>
        <Button asChild>
          <a href="/login">Voltar para o login</a>
        </Button>
      </div>
    )
  }

  // ── Loading screen ────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-5 bg-gray-50">
      <div className="bg-brand-600 rounded-2xl p-3 shadow-md">
        <Car className="h-8 w-8 text-white" />
      </div>
      <div className="flex flex-col items-center gap-3">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="text-sm text-muted-foreground">Concluindo login com Google…</p>
      </div>
    </div>
  )
}
